import type { IncomingHttpHeaders } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import { getAllTestParticipations } from '@packages/abtesting';
import type { Config, Locale } from '@packages/config';
import staticConfig from '@packages/config';
import { TrackingContext } from '@packages/tracking';
import { useUserAgentDevice } from '@packages/shared';
import type { SearchParametersInput } from '@packages/gql/generated/shopping/graphql';
import type { Device } from '@packages/themes/src/default';
import { useConfig, useCookies } from '@packages/utilities';
import { getInspiringSearchSettings } from '@packages/modules/src/Header/useInspiringSearchSettings';

import {
  decodeCategoryFilter,
  decodeOrder,
  decodePage,
  decodePromotionId,
  decodeSelectedFilterValues,
  decodeUserInteraction,
} from '../queryEncoding';
import { getChannelFromDevice } from '../hooks/useChannel';
import {
  PERSONALIZATION_COOKIE_NAME,
  parsePersonalizationString,
} from '../hooks/usePersonalizationCookie';
import { getAnonymousVisitorId, getLoggedInVisitorId } from './getVisitorIds';

const prepareHighlightArticles = (commaSeparatedArticleNumbers: string) =>
  commaSeparatedArticleNumbers
    .split(',')
    .filter((x) => x)
    .map((articleNumber) => ({ articleNumber }));

const channelMapping = {
  web: 'DESKTOP',
  tab: 'TABLET',
  mob: 'MOBILE',
} as const;

const buildSharedGqlVariables = (
  config: Config,
  query: ParsedUrlQuery,
  locale: Locale,
  device: Device,
  isBot: boolean,
  cookies: Record<string, string>,
  referrer?: string,
) => {
  const {
    host: { domain },
  } = config;

  const { isInspiringSearchEnabled, forceAiEnhancement } = getInspiringSearchSettings(
    query,
    config,
  );

  const personalizationString = cookies[PERSONALIZATION_COOKIE_NAME];

  const { segment: personalizationSegment } = parsePersonalizationString(personalizationString);

  const sessionId = cookies.SESSIONID;
  const visitorId = getAnonymousVisitorId(cookies);
  const loggedInVisitorId = getLoggedInVisitorId(cookies);

  const userInteraction = decodeUserInteraction(query);
  const categoryId = decodeCategoryFilter(query);
  const promotionId = decodePromotionId(query);
  const highlightArticles =
    typeof query.highlight === 'string' ? prepareHighlightArticles(query.highlight) : [];

  // special value of "extern" is needed for tracking landmark URL generation in the EPS
  // the actual referrer is currently only used for tracking the inspiring search
  const simplifiedReferrer = referrer?.includes(domain) ? referrer : 'extern';

  const { currentPage, pageSize: limit } = decodePage(query);
  const offset = currentPage && currentPage >= 1 ? (currentPage - 1) * limit : 0;

  const sortBy = decodeOrder(query) || 'relevance';

  return {
    params: {
      environment: {
        locale,
        channel: channelMapping[getChannelFromDevice(device)],
        isBot,
      },
      version: '22',
      constraints: {
        appliedFilters: Object.entries(decodeSelectedFilterValues(query)).map(
          ([filterId, values]) => ({
            filterId,
            values,
          }),
        ),
        ...(categoryId && { categoryId }),
      },
      ...(highlightArticles.length && {
        overrides: { highlightArticles },
      }),
      tracking: {
        ...(personalizationSegment && { personalizationSegment }),
        ...(sessionId && { sessionId }),
        ...(visitorId && { visitorId }),
        ...(loggedInVisitorId && { loggedInVisitorId }),
        ...(userInteraction && { userInteraction }),
        ...(promotionId && { promotionId }),
        ...(referrer && { referrer: simplifiedReferrer }),
      },
      abTesting: {
        testParticipations: getAllTestParticipations(cookies).map(
          ({ testId, participantId, variant }) => ({
            testId,
            participantId,
            variant: variant
              ? Object.entries(variant).map(([attribute, value]) => ({ attribute, value }))
              : null,
          }),
        ),
      },
      paging: {
        limit,
        offset,
        sortBy,
      },
      aiEnhancement: {
        suggestions:
          (forceAiEnhancement && 'ALWAYS') ||
          (isInspiringSearchEnabled && 'AUTOMATIC') ||
          'DISABLED',
      },
    } satisfies SearchParametersInput,
  };
};

export const getSharedServerSideGqlVariables = (
  query: ParsedUrlQuery,
  locale: Locale,
  headers: IncomingHttpHeaders,
  cookies: Record<string, string>,
  referrer: string | undefined,
) => {
  const device = headers['x-ua-device'] as Device;
  const isBot = headers['x-ua-bot'] !== undefined;

  return buildSharedGqlVariables(
    staticConfig,
    query,
    locale as Locale,
    device,
    isBot,
    cookies,
    referrer,
  );
};

export const useSharedClientSideGqlVariables = (referrer: string | undefined) => {
  const { query } = useRouter();
  const { locale } = useIntl();
  const config = useConfig();

  const { isBot } = useContext(TrackingContext) ?? { isBot: false };
  const device = useUserAgentDevice();
  const { getCookies } = useCookies();
  const cookies = getCookies();

  return buildSharedGqlVariables(config, query, locale as Locale, device, isBot, cookies, referrer);
};
